import qs from 'qs';
import { useSelector } from 'react-redux';
import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode
} from 'tradera/state/language/selectors';
import { useMemo } from 'react';

/**
 * Generate url for recommendation request
 * @param {string} slotId - identifier of slot
 * @param {number} pageSize - limit items fetched
 * @param {array} [seedItemIds] - item list that will act as seed for recommendation
 */
export const useRecommendationsRequestBuilder = (
    slotId,
    pageSize,
    shippingCountryCode,
    seedItemIds = []
) => {
    const userLanguage = useSelector(selectPreferredLanguageCode);
    const automaticTranslationPreferred = useSelector(
        selectAutomaticTranslationPreferred
    );
    const endpoint = '/api/webapi/discover/recommendations-v4';

    return useMemo(
        () =>
            (pageIndex = 0) => {
                const queryParameters = {
                    slot: slotId,
                    maxResults: pageSize,
                    pageNumber: pageIndex,
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred
                };

                if (!!seedItemIds && seedItemIds.length > 0) {
                    queryParameters.seedItemIds = seedItemIds;
                }

                return `${endpoint}?${qs.stringify(queryParameters, {
                    arrayFormat: 'repeat'
                })}`;
            },
        [
            automaticTranslationPreferred,
            pageSize,
            seedItemIds,
            shippingCountryCode,
            slotId,
            userLanguage
        ]
    );
};

/**
 * Default page size for recommendation carousel
 */
export const PAGE_SIZE = 12;

/**
 * Generate url for similar items request
 * @param {number} id - id of item to find similar to
 */
export const getSimilarItemsRequestUrl = (id) => {
    const endpoint = '/ma/recommendations-similar-items';
    const queryParameters = {
        id,
        pageSize: PAGE_SIZE
    };
    return `${endpoint}?${qs.stringify(queryParameters)}`;
};

/**
 * Generate url for similar items request
 * @param {number} id - id of item to find similar to
 */
export const getSimilarItemsFromShopRequestUrl = (id) => {
    const endpoint = '/ma/recommendations-similar-items-from-shop';
    const queryParameters = {
        id,
        pageSize: PAGE_SIZE
    };
    return `${endpoint}?${qs.stringify(queryParameters)}`;
};

/**
 * Generate url for similar items request
 * @param {number} id - id of item to find similar to
 * @param {number} size - limit items fetched
 * @param {string} query - query for search
 * @param {number} campaignCodeId - id of the campaign
 */
export const getSimilarItemsFromCampaignRequestUrl = (
    itemId,
    size,
    query,
    campaignCodeId
) => {
    const endpoint = '/ma/recommendations-more-items-from-campaign';
    const queryParameters = {
        itemId,
        size,
        query,
        campaignCodeId
    };
    return `${endpoint}?${qs.stringify(queryParameters)}`;
};
